import { FormikProps } from 'formik';
import countries, { Alpha3Code } from 'i18n-iso-countries';
import en from 'i18n-iso-countries/langs/en.json';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultTextAreaField } from 'src/components/mui-overrides/Textarea';
import { object, string } from 'yup';
import { Button, Divider, Skeleton, Typography, useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Grid';
import { SPV } from '../../../models/index';
import { isSmallMobileDeviceQuery } from '../../../util/responsive';
import { DefaultTextField } from '../../mui-overrides/Textfield';
import { IntegerField } from '../../mui-overrides/IntegerField';

countries.registerLocale(en);
// const phoneRegExp = new RegExp(/^((\+[1-9]{1,4}[ -]?)|(\([0-9]{2,3}\)[ -]?)|([0-9]{2,4})[ -]?)*?[0-9]{3,4}[ -]?[0-9]{3,4}$/);

export const validationSchemaSPV = object({
  address: object({
    city: string().required('City is required'),
    countryCode: string().required('Country is required'),
    street: string().required('Street is required'),
    postCode: string().required('Zip Code is required'),
  }).required(),
  companyName: string().required('Company name is required'),
  companyNumber: string().required('Company number is required'),
  companyPurpose: string().optional(),
  dseDocumentSrc: string().optional(),
  email: string().email('Invalid email address').required('Email is required'),
  owner: string().required('Owner is required'),
  taxNumber: string().required('Tax number is required'),
  taxInformation: object({
    austrianFASTNR: string().optional(),
    austrianIDENTIFIKATIONSBEGRIFF: string().optional(),
  }).optional(),
  taxOffice: string().optional(),
});

export const getInitValuesSPV = (spv: SPV | null): SpvValues => {
  const { owner, email, taxNumber, taxOffice, companyPurpose, companyName, companyNumber, address, taxInformation, dseDocumentSrc } =
    spv || {};
  return {
    address: {
      street: address?.street || undefined,
      postCode: address?.postCode || undefined,
      city: address?.city || undefined,
      countryCode: address?.countryCode || undefined,
    },
    companyName,
    companyNumber,
    companyPurpose,
    dseDocumentSrc,
    email,
    owner,
    taxNumber,
    taxOffice,
    taxInformation: {
      austrianFASTNR: taxInformation?.austrianFASTNR || undefined,
      austrianIDENTIFIKATIONSBEGRIFF: taxInformation?.austrianIDENTIFIKATIONSBEGRIFF || undefined,
    },
  };
};

export interface SpvValues {
  address?: {
    city?: string;
    countryCode?: Alpha3Code;
    postCode?: string;
    street?: string;
  };
  companyName?: string;
  companyNumber?: string;
  companyPurpose?: string;
  dseDocumentSrc?: string;
  email?: string;
  owner?: string;
  taxNumber?: string;
  taxOffice?: string;
  taxInformation?: {
    austrianFASTNR?: number;
    austrianIDENTIFIKATIONSBEGRIFF?: number;
  };
}

interface Props {
  formik: FormikProps<SpvValues>;
  spvCountry?: Alpha3Code;
  isCreating: boolean;
}

export const SpvInfo: FC<Props> = ({ formik, spvCountry, isCreating }) => {
  const { t } = useTranslation();
  const isSmallMobileDevice = useMediaQuery(isSmallMobileDeviceQuery);
  // Change to true for admin
  const disabled = false;
  return (
    <>
      <Typography gutterBottom variant="h6">
        {isCreating ? t('issuer.spv.createTitle') : t('issuer.spv.title')}
      </Typography>

      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          <Grid xs={6}>
            <DefaultTextField
              disabled={disabled}
              label={t('issuer.spv.form.companyName')}
              fieldName="companyName"
              placeholder="Add a company name"
              formik={formik}
            />
            <DefaultTextField
              disabled={disabled}
              label={t('issuer.spv.form.taxNumber')}
              fieldName="taxNumber"
              placeholder="Add a tax number"
              formik={formik}
            />
            <DefaultTextField
              disabled={disabled}
              label={t('issuer.spv.form.email')}
              fieldName="email"
              placeholder="Add an email"
              formik={formik}
            />
            {/* <DefaultTextField disabled={disabled} label="Mobile Phone" fieldName="mobilePhone" placeholder="Add a mobile phone" formik={formik} /> */}
            <DefaultTextField
              disabled={disabled}
              label={t('issuer.spv.form.owner')}
              fieldName="owner"
              placeholder="Add an owner"
              formik={formik}
            />
            <DefaultTextAreaField
              disabled={disabled}
              label={t('issuer.spv.form.companyPurpose')}
              fieldName="companyPurpose"
              placeholder="Add a company purpose"
              formik={formik}
            />
          </Grid>
          <Grid xs={6}>
            <DefaultTextField
              disabled={disabled}
              label={t('issuer.spv.form.street')}
              fieldName="address.street"
              placeholder="Add a street"
              formik={formik}
            />
            <DefaultTextField
              disabled={disabled}
              label={t('issuer.spv.form.city')}
              fieldName="address.city"
              placeholder="Add a city"
              formik={formik}
            />
            <DefaultTextField
              disabled={disabled}
              label={t('issuer.spv.form.zip')}
              fieldName="address.postCode"
              placeholder="Add a zip code"
              formik={formik}
            />
            <DefaultTextField
              disabled={disabled}
              label={t('issuer.spv.form.country')}
              fieldName="address.countryCode"
              placeholder="Add a country"
              formik={formik}
            />
            <DefaultTextField
              disabled={disabled}
              label={t('issuer.overview.companyNumber')}
              fieldName="companyNumber"
              placeholder="Add a company number"
              formik={formik}
            />
            <DefaultTextField
              disabled={disabled}
              label={t('issuer.overview.dseDocumentSrc')}
              fieldName="dseDocumentSrc"
              placeholder="Add a DSE document"
              formik={formik}
            />
          </Grid>
          {spvCountry === countries.getAlpha3Code('Austria', 'en') && (
            <>
              <Grid xs={12}>
                <Divider />
              </Grid>
              <Grid xs={6}>
                <IntegerField
                  disabled={disabled}
                  label={t('issuer.spv.form.FASTNR')}
                  fieldName="taxInformation.austrianFASTNR"
                  placeholder="Add a FASTNR"
                  formik={formik}
                />
                <DefaultTextField
                  disabled={disabled}
                  label={t('issuer.spv.form.taxOfficeNumber')}
                  fieldName="taxOffice"
                  placeholder="Tax Office"
                  formik={formik}
                />
              </Grid>
              <Grid xs={6}>
                <IntegerField
                  disabled={disabled}
                  label={t('issuer.spv.form.IDENTIFIKATIONSBEGRIFF')}
                  fieldName="taxInformation.austrianIDENTIFIKATIONSBEGRIFF"
                  placeholder="Add a IDENTIFIKATIONSBEGRIFF"
                  formik={formik}
                />
              </Grid>
            </>
          )}
        </Grid>
        {/* submit form */}
        {!disabled && (
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={formik.isSubmitting}
            style={{ gridColumn: isSmallMobileDevice ? '1 / 1' : '1 / 3', justifySelf: 'center' }}
          >
            {formik.isSubmitting ? <Skeleton variant="circular" width={24} height={24} /> : t('issuer.spv.save')}
          </Button>
        )}
      </form>
    </>
  );
};
